/**
 * ============================================================================
 * challengeService.ts - Monthly Challenges System
 * ============================================================================
 * 
 * Handles monthly poker challenges with rotating types:
 * - Hands Champion: Most hands won
 * - Pot King: Biggest single pot won
 * - Streak Master: Longest winning streak
 * - Grind Lord: Most hands played
 * 
 * Prize Pool: $15 base + (Monthly Rake × 0.5%), capped at $100
 * Top 3 Winners: 50% / 30% / 20%
 * 
 * ============================================================================
 */

import { db, auth } from './firebase';
import { API_BASE } from '../utils/apiBase';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  onSnapshot,
} from 'firebase/firestore';

// ============================================================================
// TYPES
// ============================================================================

export type ChallengeType = 'hands_won' | 'biggest_pot' | 'win_streak' | 'hands_played';

export interface Challenge {
  id: string;
  type: ChallengeType;
  name: string;
  description: string;
  icon: string;
  month: number; // 1-12
  year: number;
  startDate: Date;
  endDate: Date;
  status: 'upcoming' | 'active' | 'completed';
  prizePool: number;
  basePrize: number;
  rakeContribution: number;
  maxPrize: number;
  pkcashMultiplier: number;
  playcoinsMultiplier: number;
  winners?: ChallengeWinner[];
}

export interface ChallengeWinner {
  userId: string;
  username: string;
  avatar?: string;
  rank: number;
  score: number;
  prize: number;
}

export interface ChallengeEntry {
  odid: string;
  odidname: string;
  odidavatar?: string;
  odidusername?: string;
  score: number;
  handsWon?: number;
  biggestPot?: number;
  currentStreak?: number;
  bestStreak?: number;
  handsPlayed?: number;
  lastUpdated: Date;
}

export interface ChallengeLeaderboard {
  challenge: Challenge;
  entries: ChallengeEntry[];
  userRank?: number;
  userEntry?: ChallengeEntry;
  totalParticipants: number;
}

// Challenge configurations
export const CHALLENGE_CONFIG: Record<ChallengeType, {
  name: string;
  description: string;
  icon: string;
  pkcashMultiplier: number;
  playcoinsMultiplier: number;
  scoreField: string;
}> = {
  hands_won: {
    name: 'Hands Champion',

    description: 'Win the most hands this month',
    icon: '',

    pkcashMultiplier: 2,
    playcoinsMultiplier: 1,
    scoreField: 'handsWon',
  },
  biggest_pot: {
    name: 'Pot King',

    description: 'Win the biggest single pot',
    icon: '',

    pkcashMultiplier: 1,
    playcoinsMultiplier: 0, // PKCash only
    scoreField: 'biggestPot',
  },
  win_streak: {
    name: 'Streak Master',

    description: 'Build the longest winning streak',
    icon: '',

    pkcashMultiplier: 2,
    playcoinsMultiplier: 1,
    scoreField: 'bestStreak',
  },
  hands_played: {
    name: 'Grind Lord',

    description: 'Play the most hands',
    icon: '',

    pkcashMultiplier: 1.5,
    playcoinsMultiplier: 1,
    scoreField: 'handsPlayed',
  },
};

// Rotation order for challenges
export const CHALLENGE_ROTATION: ChallengeType[] = [
  'hands_won',
  'biggest_pot', 
  'win_streak',
  'hands_played',
];

// ============================================================================
// API CONFIGURATION
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

// ============================================================================
// CHALLENGE SERVICE CLASS
// ============================================================================

class ChallengeService {
  
  /**
   * Get current month's challenge ID
   */
  getCurrentChallengeId(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Get challenge type for a given month (auto-rotation)
   */
  getChallengeTypeForMonth(month: number, year: number): ChallengeType {
    // Rotate based on month index (Jan 2025 = 0, Feb 2025 = 1, etc.)
    const monthsSinceStart = (year - 2025) * 12 + (month - 1);
    const typeIndex = monthsSinceStart % CHALLENGE_ROTATION.length;
    return CHALLENGE_ROTATION[typeIndex];
  }

  /**
   * Get the current active challenge
   */
  async getCurrentChallenge(): Promise<Challenge | null> {
    try {
      const challengeId = this.getCurrentChallengeId();
      const challengeRef = doc(db, 'challenges', challengeId);
      const challengeSnap = await getDoc(challengeRef);

      if (!challengeSnap.exists()) {
        // Challenge doesn't exist yet - it will be created by scheduled function
        return null;
      }

      const data = challengeSnap.data();
      return {
        id: challengeSnap.id,
        ...data,
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate() || new Date(),
      } as Challenge;
    } catch (error) {
      console.error('[ChallengeService] Error getting current challenge:', error);
      return null;
    }
  }

  /**
   * Get challenge leaderboard with rankings
   */
  async getChallengeLeaderboard(challengeId?: string, maxEntries: number = 10): Promise<ChallengeLeaderboard | null> {
    try {
      const id = challengeId || this.getCurrentChallengeId();
      
      // Get challenge info
      const challengeRef = doc(db, 'challenges', id);
      const challengeSnap = await getDoc(challengeRef);

      if (!challengeSnap.exists()) {
        return null;
      }

      const challengeData = challengeSnap.data();
      const challenge: Challenge = {
        id: challengeSnap.id,
        ...challengeData,
        startDate: challengeData.startDate?.toDate() || new Date(),
        endDate: challengeData.endDate?.toDate() || new Date(),
      } as Challenge;

      // Get leaderboard entries
      const entriesRef = collection(db, 'challenges', id, 'entries');
      const entriesQuery = query(
        entriesRef,
        orderBy('score', 'desc'),
        limit(maxEntries)
      );
      const entriesSnap = await getDocs(entriesQuery);

      //  FIX: Fetch fresh user data for each entry and filter out deleted users

      const entriesWithUsers = await Promise.all(
        entriesSnap.docs.map(async (entryDoc) => {
          const entryData = entryDoc.data();
          const odid = entryDoc.id;
          
          // Fetch current user profile - if user doesn't exist, skip them
          try {
            const userDoc = await getDoc(doc(db, 'users', odid));
            if (!userDoc.exists()) {
              // User was deleted - skip this entry
              console.log(`[ChallengeService] Skipping deleted user ${odid}`);
              return null;
            }
            
            const userData = userDoc.data();
            //  ALWAYS use username from user doc, never displayName

            const currentUsername = userData.username || 'Unknown';
            const currentAvatar = userData.photoURL || userData.avatar || entryData.odidavatar;
            
            return {
              odid,
              odidname: currentUsername,
              odidusername: currentUsername,
              odidavatar: currentAvatar,
              score: entryData.score || 0,
              handsWon: entryData.handsWon,
              biggestPot: entryData.biggestPot,
              currentStreak: entryData.currentStreak,
              bestStreak: entryData.bestStreak,
              handsPlayed: entryData.handsPlayed,
              lastUpdated: entryData.lastUpdated?.toDate() || new Date(),
            } as ChallengeEntry;
          } catch (err) {
            console.warn(`[ChallengeService] Could not fetch user ${odid}:`, err);
            return null;
          }
        })
      );
      
      // Filter out null entries (deleted users)
      const entries: ChallengeEntry[] = entriesWithUsers.filter((e): e is ChallengeEntry => e !== null);

      // Get total participants count
      const allEntriesSnap = await getDocs(collection(db, 'challenges', id, 'entries'));
      const totalParticipants = allEntriesSnap.size;

      // Get current user's entry and rank
      let userRank: number | undefined;
      let userEntry: ChallengeEntry | undefined;
      
      const currentUser = auth.currentUser;
      if (currentUser) {
        const userEntryRef = doc(db, 'challenges', id, 'entries', currentUser.uid);
        const userEntrySnap = await getDoc(userEntryRef);
        
        if (userEntrySnap.exists()) {
          const entryData = userEntrySnap.data();
          
          //  FIX: Get fresh user profile data - always use username

          let currentUsername = 'Unknown';
          let currentAvatar = entryData.odidavatar;
          
          try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              //  ALWAYS use username, never displayName

              currentUsername = userData.username || 'Unknown';
              currentAvatar = userData.photoURL || userData.avatar || currentAvatar;
            }
          } catch (err) {
            // Use stored data as fallback
            currentUsername = entryData.odidusername || entryData.odidname || 'Unknown';
          }
          
          userEntry = {
            odid: userEntrySnap.id,
            odidname: currentUsername,
            odidusername: currentUsername,
            odidavatar: currentAvatar,
            score: entryData.score || 0,
            handsWon: entryData.handsWon,
            biggestPot: entryData.biggestPot,
            currentStreak: entryData.currentStreak,
            bestStreak: entryData.bestStreak,
            handsPlayed: entryData.handsPlayed,
            lastUpdated: entryData.lastUpdated?.toDate() || new Date(),
          } as ChallengeEntry;

          // Calculate user's rank
          const higherScoreQuery = query(
            entriesRef,
            where('score', '>', userEntry.score)
          );
          const higherScoreSnap = await getDocs(higherScoreQuery);
          userRank = higherScoreSnap.size + 1;
        }
      }

      return {
        challenge,
        entries,
        userRank,
        userEntry,
        totalParticipants,
      };
    } catch (error) {
      console.error('[ChallengeService] Error getting leaderboard:', error);
      return null;
    }
  }

  /**
   * Subscribe to live leaderboard updates
   */
  subscribeToLeaderboard(
    challengeId: string,
    callback: (entries: ChallengeEntry[]) => void,
    maxEntries: number = 10
  ): () => void {
    const entriesRef = collection(db, 'challenges', challengeId, 'entries');
    const entriesQuery = query(
      entriesRef,
      orderBy('score', 'desc'),
      limit(maxEntries)
    );

    return onSnapshot(entriesQuery, (snapshot) => {
      const entries: ChallengeEntry[] = snapshot.docs.map(doc => ({
        odid: doc.id,
        ...doc.data(),
        lastUpdated: doc.data().lastUpdated?.toDate() || new Date(),
      } as ChallengeEntry));
      callback(entries);
    });
  }

  /**
   * Get user's challenge history (past challenges they participated in)
   */
  async getUserChallengeHistory(userId?: string): Promise<Challenge[]> {
    try {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) return [];

      const challengesRef = collection(db, 'challenges');
      const challengesQuery = query(
        challengesRef,
        where('status', '==', 'completed'),
        orderBy('endDate', 'desc'),
        limit(12) // Last 12 months
      );
      
      const challengesSnap = await getDocs(challengesQuery);
      const challenges: Challenge[] = [];

      for (const challengeDoc of challengesSnap.docs) {
        // Check if user participated
        const entryRef = doc(db, 'challenges', challengeDoc.id, 'entries', uid);
        const entrySnap = await getDoc(entryRef);
        
        if (entrySnap.exists()) {
          const data = challengeDoc.data();
          challenges.push({
            id: challengeDoc.id,
            ...data,
            startDate: data.startDate?.toDate() || new Date(),
            endDate: data.endDate?.toDate() || new Date(),
          } as Challenge);
        }
      }

      return challenges;
    } catch (error) {
      console.error('[ChallengeService] Error getting user history:', error);
      return [];
    }
  }

  /**
   * Get prize breakdown for top 3
   */
  getPrizeBreakdown(prizePool: number): { first: number; second: number; third: number } {
    return {
      first: Math.floor(prizePool * 0.50 * 100) / 100,
      second: Math.floor(prizePool * 0.30 * 100) / 100,
      third: Math.floor(prizePool * 0.20 * 100) / 100,
    };
  }

  /**
   * Format score for display based on challenge type
   */
  formatScore(score: number, type: ChallengeType): string {
    switch (type) {
      case 'biggest_pot':
        return `$${score.toFixed(2)}`;
      case 'hands_won':
      case 'hands_played':
        return `${score.toLocaleString()} hands`;
      case 'win_streak':
        return `${score} wins`;
      default:
        return score.toString();
    }
  }

  /**
   * Get time remaining in current challenge
   */
  getTimeRemaining(challenge: Challenge): string {
    const now = new Date();
    const end = new Date(challenge.endDate);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    }
    return `${hours}h remaining`;
  }

  /**
   * Get prize pool from challenge document
   * (Prize pool is updated daily by Cloud Function, no direct rake query needed)
   */
  async getCurrentPrizePool(): Promise<{
    base: number;
    rakeContribution: number;
    total: number;
    maxReached: boolean;
  }> {
    try {
      const BASE_PRIZE = 15;
      const MAX_PRIZE = 100;

      // Get prize pool from challenge document (updated daily by server)
      const challenge = await this.getCurrentChallenge();
      
      if (!challenge) {
        return { base: BASE_PRIZE, rakeContribution: 0, total: BASE_PRIZE, maxReached: false };
      }

      const rakeContribution = challenge.rakeContribution || 0;
      const total = challenge.prizePool || BASE_PRIZE;

      return {
        base: BASE_PRIZE,
        rakeContribution,
        total,
        maxReached: total >= MAX_PRIZE,
      };
    } catch (error) {
      console.error('[ChallengeService] Error getting prize pool:', error);
      return { base: 15, rakeContribution: 0, total: 15, maxReached: false };
    }
  }
}

// ============================================================================
// EXPORT
// ============================================================================

const challengeService = new ChallengeService();
export default challengeService;